
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Chip,
  Divider,
  Button,
  Stack,
} from '@mui/material';
import dayjs from 'dayjs';

/* --------- tokens design alignés avec ProfileDetails ---------------------- */
const OK        = '#37d079';
const NOK       = '#ff6b6b';
const LABEL     = '#6c757d';
const BADGE_BG  = '#e6f1ff';
const BADGE_TX  = '#007bff';
const SK_BG     = '#ece8ff';

/* --------- helpers -------------------------------------------------------- */
const isExpired = (d) =>
  d && dayjs(d, 'YYYY-MM-DD').isBefore(dayjs(), 'day');

/* --------- ligne grille “Label / Valeur” ---------------------------------- */
const Row = ({ label, value }) => (
  <>
    <Box
      display="grid"
      gridTemplateColumns="150px 1fr"
      alignItems="center"
      py={0.75}
    >
      <Typography sx={{ color: LABEL, fontWeight: 400, fontSize: '0.875rem' }}>
        {label}
      </Typography>
      {typeof value === 'string' || typeof value === 'number' ? (
        <Typography sx={{ color: '#212529', fontWeight: 500, fontSize: '0.875rem' }}>
          {value}
        </Typography>
      ) : (
        value
      )}
    </Box>
    <Divider />
  </>
);

/* --------- composant principal ------------------------------------------- */
export default function AppelOffreDetails({
  offre,
  onApply    = () => {},
  canApply   = false,
  hasApplied = false,
}) {
  const [moreDesc, setMoreDesc] = useState(false);
  const [moreComm, setMoreComm] = useState(false);

  if (!offre) return null;
  const expired = isExpired(offre.date_cloture_offre);

  return (
    <Box
      sx={{
        width: '100%',         // pleine largeur
        maxWidth: '100%',
        p: 4,
        bgcolor: '#fff',
        borderRadius: 2,
        boxShadow: '0 2px 6px rgba(0,0,0,.08)',
        alignSelf: 'flex-start' // s’aligne à gauche si le parent est flex
      }}
    >
      {/* --------- header -------------------------------------------------- */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Typography variant="h5" fontWeight={700}>
          {offre.titre}
        </Typography>
        <Chip
          label={expired ? 'Expiré' : 'Ouvert'}
          size="small"
          sx={{
            bgcolor: expired ? NOK : OK,
            color: '#fff',
            fontWeight: 600,
            fontSize: '0.75rem',
            borderRadius: '16px',
          }}
        />
      </Box>

      <Divider />

      {/* --------- meta rapide ------------------------------------------- */}
      <Row label="Lieu"         value={offre.lieu_prestation  || 'N/A'} />
      <Row label="Référence"    value={offre.reference        || 'N/A'} />
      <Row label="Réf. client"  value={offre.reference_client || 'N/A'} />
      <Row label="Publication"  value={offre.date_publication  || 'N/A'} />
      <Row
        label="Clôture"
        value={
          <Typography
            component="span"
            sx={{
              color: expired ? 'error.main' : 'success.main',
              fontWeight: 600,
              fontSize: '0.875rem',
            }}
          >
            {offre.date_cloture_offre || 'N/A'}
          </Typography>
        }
      />

      {/* --------- chips Tarif + Expérience ------------------------------ */}
      <Box mt={2} display="flex" flexWrap="wrap" gap={1}>
        <Chip
          label={offre.tjm ? `${offre.tjm} € / jour` : 'Tarif non spécifié'}
          size="small"
          sx={{
            bgcolor: BADGE_BG,
            color: BADGE_TX,
            fontWeight: 600,
            fontSize: '0.75rem',
            px: 1.5,
            height: 28,
            borderRadius: '16px',
            boxShadow: '0 1px 2px rgba(0,0,0,.1)',
          }}
        />
        <Chip
          label={
            offre.annee_experiences
              ? `${offre.annee_experiences} an(s) d'expérience`
              : 'Expérience non spécifiée'
          }
          size="small"
          sx={{
            bgcolor: SK_BG,
            color: '#2F4E8B',
            fontWeight: 600,
            fontSize: '0.75rem',
            px: 1.5,
            height: 28,
            borderRadius: '16px',
            boxShadow: '0 1px 2px rgba(0,0,0,.1)',
          }}
        />
      </Box>

      <Divider sx={{ my: 2 }} />

      {/* --------- Descriptif -------------------------------------------- */}
      <Box py={1} display="grid" gridTemplateColumns="150px 1fr" alignItems="start">
        <Typography sx={{ color: LABEL, fontWeight: 400, fontSize: '0.875rem', mb: 0.5 }}>
          Descriptif
        </Typography>
        <Box>
          <Typography whiteSpace="pre-line" sx={{ fontSize: '0.875rem' }}>
            {!moreDesc && offre.descriptif?.length > 400
              ? `${offre.descriptif.slice(0, 400)}…`
              : offre.descriptif || 'Non spécifié'}
          </Typography>
          {offre.descriptif?.length > 400 && (
            <Button
              size="small"
              onClick={() => setMoreDesc(!moreDesc)}
              sx={{
                mt: 0.75,
                textTransform: 'none',
                bgcolor: BADGE_BG,
                color: BADGE_TX,
                fontWeight: 600,
                borderRadius: '16px',
                fontSize: 12,
                px: 2,
                py: 0.3,
                '&:hover': { bgcolor: BADGE_BG, opacity: 0.85 },
              }}
            >
              {moreDesc ? 'Voir moins' : 'Voir plus'}
            </Button>
          )}
        </Box>
      </Box>

      <Divider />

      {/* --------- Commentaire ------------------------------------------- */}
      <Box py={1} display="grid" gridTemplateColumns="150px 1fr" alignItems="start">
        <Typography sx={{ color: LABEL, fontWeight: 400, fontSize: '0.875rem', mb: 0.5 }}>
          Commentaire
        </Typography>
        <Box>
          <Typography whiteSpace="pre-line" sx={{ fontSize: '0.875rem' }}>
            {!moreComm && offre.commentaire?.length > 400
              ? `${offre.commentaire.slice(0, 400)}…`
              : offre.commentaire || 'Non spécifié'}
          </Typography>
          {offre.commentaire?.length > 400 && (
            <Button
              size="small"
              onClick={() => setMoreComm(!moreComm)}
              sx={{
                mt: 0.75,
                textTransform: 'none',
                bgcolor: BADGE_BG,
                color: BADGE_TX,
                fontWeight: 600,
                borderRadius: '16px',
                fontSize: 12,
                px: 2,
                py: 0.3,
                '&:hover': { bgcolor: BADGE_BG, opacity: 0.85 },
              }}
            >
              {moreComm ? 'Voir moins' : 'Voir plus'}
            </Button>
          )}
        </Box>
      </Box>

      <Divider />

      {/* --------- Actions ----------------------------------------------- */}
      <Stack direction="row" spacing={2} justifyContent="flex-end" mt={3}>
        {canApply && !expired && (
          <Button
            variant="contained"
            disabled={hasApplied}
            onClick={onApply}
            sx={{
              textTransform: 'none',
              bgcolor: '#2F4E8B',
              '&:hover': { bgcolor: '#0f214d' },
              fontWeight: 'bold',
              py: 1,
              px: 3,
              borderRadius: '50px',
              color: '#fff',
            }}
          >
            {hasApplied ? 'Déjà postulé' : 'Postuler'}
          </Button>
        )}
      </Stack>
    </Box>
  );
}
